import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Trash2, UserPlus, Users2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  deviceGroups as mockGroups,
  type ActiveUser,
  type DeviceGroup,
} from "../../data";
import {
  addUserToGroup,
  createGroup,
  listGroups,
  listUsers,
  removeUserFromGroup,
  renameGroup,
} from "../../api/endpoints";
import { toActiveUser, toDeviceGroup } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { ApiError } from "../../api/types";

// Разделы прав, по которым группа может видеть приложения / фичи каталога.
const PERMISSION_SECTIONS: { key: string; label: string; hint: string }[] = [
  { key: "catalog",    label: "Каталог приложений",      hint: "Видит список и карточки приложений" },
  { key: "updates",    label: "Получает обновления",     hint: "Push-уведомления о новых версиях" },
  { key: "install",    label: "Установка приложений",    hint: "Может устанавливать из каталога" },
  { key: "uninstall",  label: "Удаление приложений",     hint: "Может удалять с устройства" },
  { key: "vpn",        label: "VPN / корп. сеть",        hint: "Видит сетевые приложения" },
  { key: "office",     label: "Офисный блок",            hint: "Документы, почта, ВКС" },
  { key: "field",      label: "Полевой блок",            hint: "Геоучёт, аудит, обходы" },
  { key: "finance",    label: "Финансовый блок",         hint: "Отчётность, учёт ОС" },
  { key: "security",   label: "Безопасность",            hint: "Корпоративные политики безопасности" },
  { key: "developer",  label: "Инструменты DEV",         hint: "Доступ к тестовым приложениям" },
];

// Демо-права по умолчанию для каждой группы.
const DEFAULT_PERMS: Record<string, Record<string, boolean>> = {
  ALL:    { catalog: true,  updates: true,  install: true,  uninstall: true,  vpn: true,  office: true,  field: true,  finance: false, security: true,  developer: false },
  OFFICE: { catalog: true,  updates: true,  install: true,  uninstall: true,  vpn: true,  office: true,  field: false, finance: false, security: true,  developer: false },
  FIELD:  { catalog: true,  updates: true,  install: true,  uninstall: false, vpn: true,  office: false, field: true,  finance: false, security: true,  developer: false },
  FIN:    { catalog: true,  updates: true,  install: true,  uninstall: true,  vpn: true,  office: true,  field: false, finance: true,  security: true,  developer: false },
  DEV:    { catalog: true,  updates: true,  install: true,  uninstall: true,  vpn: true,  office: true,  field: true,  finance: false, security: true,  developer: true  },
};

function permsFor(code: string): Record<string, boolean> {
  return DEFAULT_PERMS[code] ?? Object.fromEntries(PERMISSION_SECTIONS.map((s) => [s.key, s.key === "catalog"]));
}

export default function GroupsPage() {
  const qc = useQueryClient();
  const push = useToast();

  const [selected, setSelected] = useState<string>("ALL");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Локальные изменения прав (по коду группы), пока не сохранены — для демо хватает.
  const [permState, setPermState] = useState<Record<string, Record<string, boolean>>>({});

  const groupsQ = useQuery({ queryKey: ["groups"], queryFn: listGroups });
  const usersQ = useQuery({
    queryKey: ["users", selected],
    queryFn: () => listUsers(selected === "ALL" ? undefined : selected),
  });

  const groups = useApiFallback<DeviceGroup[]>(
    groupsQ.data?.map(toDeviceGroup),
    groupsQ.isError,
    mockGroups,
  );

  const apiUsers: ActiveUser[] | undefined = usersQ.data?.map(toActiveUser);
  // Пустой fallback — без мерцания демо-пользователей при reload.
  const users = useApiFallback<ActiveUser[]>(apiUsers, usersQ.isError, []);

  const filteredUsers = useMemo(() => {
    if (!q.trim()) return users.value;
    const t = q.toLowerCase();
    return users.value.filter(
      (u) =>
        u.fullName.toLowerCase().includes(t) ||
        u.login.toLowerCase().includes(t) ||
        u.role.toLowerCase().includes(t),
    );
  }, [users.value, q]);

  const selectedGroup = groups.value.find((g) => g.code === selected) ?? groups.value[0];
  const currentPerms = permState[selected] ?? permsFor(selected);

  const togglePerm = (key: string) => {
    setPermState((prev) => {
      const base = prev[selected] ?? permsFor(selected);
      return { ...prev, [selected]: { ...base, [key]: !base[key] } };
    });
  };

  // ─── мутации ───────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: createGroup,
    onSuccess: async (g) => {
      await qc.invalidateQueries({ queryKey: ["groups"] });
      push({ kind: "success", text: `Группа «${g.name}» создана` });
      setSelected(g.code);
      setCreateOpen(false);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Ошибка создания") : "Ошибка создания";
      push({ kind: "warn", text: msg });
    },
  });

  const renameMut = useMutation({
    mutationFn: ({ code, name }: { code: string; name: string }) => renameGroup(code, name),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["groups"] });
      push({ kind: "success", text: "Имя группы обновлено" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось переименовать") : "Не удалось переименовать";
      push({ kind: "warn", text: msg });
    },
  });

  const addMut = useMutation({
    mutationFn: ({ code, login }: { code: string; login: string }) => addUserToGroup(code, login),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users", selected] });
      await qc.invalidateQueries({ queryKey: ["groups"] });
      push({ kind: "success", text: "Пользователь добавлен в группу" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось добавить") : "Не удалось добавить";
      push({ kind: "warn", text: msg });
    },
  });

  const removeMut = useMutation({
    mutationFn: ({ code, login }: { code: string; login: string }) => removeUserFromGroup(code, login),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users", selected] });
      await qc.invalidateQueries({ queryKey: ["groups"] });
      push({ kind: "warn", text: "Пользователь удалён из группы" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось удалить") : "Не удалось удалить";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Группы устройств
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {groups.value.length} групп · права по разделам и состав
          </div>
        </div>
        <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>
          Создать группу
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
        {/* ── Левая колонка — список групп ─────────────────────────────── */}
        <section className="space-y-2">
          <h2 className="px-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-faint)]">
            Группы
          </h2>
          <div className={"flex flex-col gap-2 " + (groupsQ.isLoading ? "opacity-60" : "")}>
            {groups.value.map((g) => (
              <GroupCard
                key={g.code}
                group={g}
                active={selected === g.code}
                onClick={() => setSelected(g.code)}
                onRename={(name) => renameMut.mutate({ code: g.code, name })}
              />
            ))}
          </div>
        </section>

        {/* ── Правая колонка — состав + права ─────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-3">
          {/* Состав группы */}
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">
                  {selectedGroup?.name ?? "Группа"}
                </h2>
                <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
                  {filteredUsers.length} {pluralize(filteredUsers.length, ["пользователь", "пользователя", "пользователей"])} в составе
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-[240px]">
                  <Search
                    size={13}
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
                  />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Поиск участника"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 pl-7 pr-3 text-[12.5px] text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
                  />
                </div>
                <Btn
                  icon={UserPlus}
                  size="sm"
                  onClick={() => {
                    const login = window.prompt("Логин пользователя");
                    if (!login) return;
                    addMut.mutate({ code: selected, login: login.trim() });
                  }}
                >
                  Добавить
                </Btn>
              </div>
            </div>

            <div className={"-mx-1 mt-4 overflow-x-auto " + (usersQ.isLoading ? "opacity-60" : "")}>
              <table className="w-full min-w-[560px] text-[12.5px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                    <Th>Пользователь</Th>
                    <Th>Логин</Th>
                    <Th>Роль</Th>
                    <Th>Устройство</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, i) => (
                    <tr
                      key={u.login}
                      style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                      className="stagger-up border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface-2)]"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--color-surface-3)] text-[10.5px] font-medium text-[var(--color-fg)]">
                            {u.initials}
                          </span>
                          {u.fullName}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{u.login}</td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <RoleChip role={u.role} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-[var(--color-fg-muted)]">{u.device}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-right">
                        <button
                          onClick={() => removeMut.mutate({ code: selected, login: u.login })}
                          className="rounded-md p-1.5 text-[var(--color-fg-muted)] transition-colors hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
                          aria-label="Удалить из группы"
                          title="Удалить из группы"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!filteredUsers.length && (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                        В этой группе пока никого нет.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Права группы */}
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-[14px] font-semibold text-[var(--color-fg)]">
                  <Users2 size={14} className="text-[var(--color-fg-muted)]" />
                  Права группы
                </h2>
                <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
                  Какие разделы каталога видят устройства этой группы
                </p>
              </div>
              <span className="rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 font-mono text-[10.5px] text-[var(--color-fg-muted)]">
                {selected}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PERMISSION_SECTIONS.map((s) => {
                const on = !!currentPerms[s.key];
                return (
                  <label
                    key={s.key}
                    className={
                      "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-[12.5px] transition-colors " +
                      (on
                        ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5"
                        : "border-[var(--color-border)] hover:bg-[var(--color-surface-2)]")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => togglePerm(s.key)}
                      className="mt-0.5 h-3.5 w-3.5 accent-[var(--color-brand)]"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-[var(--color-fg)]">{s.label}</div>
                      <div className="mt-0.5 text-[11px] text-[var(--color-fg-muted)]">{s.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <CreateGroupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(payload) => createMut.mutate(payload)}
        pending={createMut.isPending}
      />
    </>
  );
}

// ── вспомогательные компоненты ────────────────────────────────────────────

function GroupCard({
  group,
  active,
  onClick,
  onRename,
}: {
  group: DeviceGroup;
  active: boolean;
  onClick: () => void;
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== group.name) onRename(next);
    else setDraft(group.name);
  };

  return (
    <div
      onClick={onClick}
      onDoubleClick={() => {
        setDraft(group.name);
        setEditing(true);
      }}
      className={
        "flex w-full cursor-pointer items-start gap-3 rounded-xl border p-3 text-left transition-all duration-150 " +
        (active
          ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 shadow-[0_4px_12px_-4px_rgba(31,58,138,0.18)]"
          : "border-[var(--color-border)] bg-[var(--color-surface)] hover:-translate-y-0.5 hover:border-[var(--color-fg-muted)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)]")
      }
    >
      <span
        className={
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg font-mono text-[10.5px] font-semibold " +
          (active
            ? "bg-[var(--color-brand)] text-white"
            : "bg-[var(--color-surface-3)] text-[var(--color-fg)]")
        }
      >
        {group.letters}
      </span>
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(group.name);
                setEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-md border border-[var(--color-brand)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[13px] font-medium text-[var(--color-fg)] outline-none"
          />
        ) : (
          <div className="truncate text-[13px] font-medium text-[var(--color-fg)]" title="Двойной клик — переименовать">
            {group.name}
          </div>
        )}
        <div className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
          {group.devices.toLocaleString("ru-RU")} устройств · {group.appsVisible} приложений
        </div>
      </div>
    </div>
  );
}

function CreateGroupModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { code: string; name: string; letters: string; description: string }) => void;
  pending: boolean;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [letters, setLetters] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- сброс формы при закрытии модалки
      setCode("");
      setName("");
      setLetters("");
      setDescription("");
    }
  }, [open]);

  const canSubmit = code.trim().length >= 2 && name.trim().length >= 2 && !pending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Users2 size={16} className="text-[var(--color-brand)]" />
          Новая группа устройств
        </span>
      }
      subtitle="Группа объединяет устройства и определяет видимость приложений"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn
            variant="primary"
            onClick={() => {
              if (!canSubmit) return;
              onSubmit({
                code: code.trim().toUpperCase(),
                name: name.trim(),
                letters: (letters.trim() || code.trim()).slice(0, 4).toUpperCase(),
                description: description.trim(),
              });
            }}
          >
            {pending ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px]">
        <Field label="Код" hint="латиница, без пробелов">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
            placeholder="OFFICE"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Название">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Офисные пользователи"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Буквы плашки" hint="3-4 символа">
          <input
            value={letters}
            onChange={(e) => setLetters(e.target.value.toUpperCase().slice(0, 4))}
            placeholder="OFF"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Описание">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Описание назначения группы"
            rows={3}
            className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </span>
        {hint && <span className="text-[10.5px] text-[var(--color-fg-faint)]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function RoleChip({ role }: { role: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    "разработчик": { bg: "var(--color-warning-bg)", fg: "var(--color-warning)" },
    "пользователь": { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" },
    "лок. админ":  { bg: "var(--color-brand)/15", fg: "var(--color-brand)" },
    "глоб. админ": { bg: "var(--color-danger-bg)", fg: "var(--color-danger)" },
  };
  const m = map[role] ?? { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" };
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
      style={{ background: m.bg, color: m.fg }}
    >
      {role}
    </span>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function pluralize(n: number, forms: [string, string, string]) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}
